import { useEffect, useState } from "react";
import {
  Plus, Loader2, Upload, Ban, Copy, Trash2, Users, ShieldCheck, Download,
} from "lucide-react";
import { downloadFromUrl } from "@/lib/download";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDate } from "./shared";

function ListDetailDialog({ listId, onClose }) {
  const [row, setRow] = useState(null);
  useEffect(() => { api.get(`/outbound/lists/${listId}`).then((r) => setRow(r.data)).catch(() => {}); }, [listId]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="ai-outbound-list-detail">
        <DialogHeader>
          <DialogTitle>{row?.name || "List"}</DialogTitle>
          <DialogDescription>{row?.recipients?.length || 0} recipient(s)</DialogDescription>
        </DialogHeader>
        {!row ? <div className="text-slate-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">Name</th>
                  <th className="px-2 py-1.5 text-left">Phone</th>
                  <th className="px-2 py-1.5 text-left">Email</th>
                  <th className="px-2 py-1.5 text-left">Company</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(row.recipients || []).map((r) => (
                  <tr key={r.recipient_id}>
                    <td className="px-2 py-1.5 text-slate-700">{r.name || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-700">{r.phone || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600">{r.email || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-600">{r.company || "—"}</td>
                  </tr>
                ))}
                {(row.recipients || []).length === 0 && (
                  <tr><td colSpan={4} className="px-2 py-6 text-center text-xs text-slate-400">No recipients yet — upload a CSV.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RecipientListsPanel({ lists, onReload }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [openId, setOpenId] = useState(null);
  const [uploading, setUploading] = useState(null);

  const createList = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/outbound/lists", { name });
      toast.success("List created");
      setName(""); setCreating(false); onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delList = async (l) => {
    if (!window.confirm(`Delete list "${l.name}" and all its recipients?`)) return;
    try { await api.delete(`/outbound/lists/${l.list_id}`); toast.success("Deleted"); onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const upload = async (l, file) => {
    if (!file) return;
    setUploading(l.list_id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/outbound/lists/${l.list_id}/upload-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added ${data.added} recipient(s)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(null); }
  };

  const convertToDnc = async (l) => {
    if (!window.confirm(`Convert every phone number in "${l.name}" into a new Do-Not-Call list?`)) return;
    try {
      const { data } = await api.post(`/outbound/lists/${l.list_id}/convert-to-dnc`, {});
      toast.success(`Created DNC list "${data.name}" with ${data.added} entry(ies)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [scrubBusy, setScrubBusy] = useState(null);
  const scrub = async (l, { confirm_cost = false } = {}) => {
    const t = toast.loading("Checking DNC scrub cost…");
    try {
      setScrubBusy(l.list_id);
      const { data } = await api.post(`/outbound/lists/${l.list_id}/dnc-scrub`,
        { confirm_cost });
      if (data.requires_confirmation) {
        toast.dismiss(t);
        const ok = window.confirm(`${data.message}\n\nProceed with the DNC scrub?`);
        if (ok) return scrub(l, { confirm_cost: true });
        return;
      }
      const r = data.results || {};
      toast.success(
        `Scrubbed ${data.numbers_total} · ${r.clean || 0} clean · ${r.tcpa_litigator || 0} TCPA · ${r.federal_dnc || 0} DNC · ${r.invalid || 0} invalid · $${(data.cost_usd || 0).toFixed(2)} spend`,
        { id: t, duration: 8000 });
      onReload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scrub failed", { id: t });
    } finally { setScrubBusy(null); }
  };

  return (
    <div className="space-y-4">
      <Card className="border border-slate-200 p-4" data-testid="ai-outbound-lists-create">
        {creating ? (
          <div className="flex items-center gap-2">
            <Input data-testid="new-list-name" placeholder="List name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createList()} />
            <Button onClick={createList} data-testid="new-list-save">Create</Button>
            <Button variant="ghost" onClick={() => { setCreating(false); setName(""); }} data-testid="new-list-cancel">Cancel</Button>
          </div>
        ) : (
          <Button onClick={() => setCreating(true)} variant="outline" data-testid="new-list-btn"><Plus className="h-4 w-4" /> New recipient list</Button>
        )}
      </Card>

      {lists.length === 0 ? (
        <Card className="border border-dashed border-slate-200 p-10 text-center" data-testid="ai-outbound-empty-lists">
          <Users className="mx-auto h-8 w-8 text-slate-300" />
          <div className="mt-2 font-heading text-sm font-semibold text-slate-700">No recipient lists yet</div>
          <p className="mt-1 text-xs text-slate-400">Create a list, then upload a CSV with columns: name, phone, email, company.</p>
        </Card>
      ) : (
        <Card className="border border-slate-200 p-0 overflow-hidden" data-testid="ai-outbound-lists-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">List</th>
                <th className="px-3 py-2 text-right">Recipients</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lists.map((l) => (
                <tr key={l.list_id} className="hover:bg-slate-50" data-testid={`ai-outbound-list-row-${l.list_id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setOpenId(l.list_id)} className="text-left font-medium text-slate-800 hover:text-primary" data-testid={`ai-outbound-list-open-${l.list_id}`}>{l.name}</button>
                    {l.description && <div className="text-xs text-slate-400">{l.description}</div>}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">{l.recipient_count || 0}</td>
                  <td className="px-3 py-2 text-slate-600">{l.owner_name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <input type="file" accept=".csv" className="hidden"
                      id={`file-${l.list_id}`}
                      onChange={(e) => upload(l, e.target.files?.[0])} data-testid={`ai-outbound-list-upload-input-${l.list_id}`} />
                    <label htmlFor={`file-${l.list_id}`}
                      className="mr-2 inline-flex cursor-pointer items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:border-primary/40 hover:text-primary"
                      data-testid={`ai-outbound-list-upload-${l.list_id}`}>
                      {uploading === l.list_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      CSV
                    </label>
                    <button data-testid={`ai-outbound-list-convert-dnc-${l.list_id}`} onClick={() => convertToDnc(l)}
                      className="mr-2 text-slate-300 hover:text-red-500" title="Convert to Do-Not-Call list"><Ban className="h-4 w-4" /></button>
                    <button data-testid={`ai-outbound-list-dnc-scrub-${l.list_id}`}
                            onClick={() => scrub(l)}
                            disabled={scrubBusy === l.list_id}
                            className="mr-2 inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            title="Scrub against RealValidito DNC + line-type (billable — $0.015 per fresh lookup)">
                      {scrubBusy === l.list_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                      Scrub DNC
                      {l.last_dnc_scrub_at && <span className="text-emerald-600/70 text-[9px]">· ${(l.dnc_scrub_spend_usd || 0).toFixed(2)}</span>}
                    </button>
                    <button data-testid={`ai-outbound-list-delete-${l.list_id}`} onClick={() => delList(l)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {openId && <ListDetailDialog listId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function DncListDetailDialog({ listId, allLists, onClose, onChanged }) {
  const [row, setRow] = useState(null);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [copySource, setCopySource] = useState("");

  const load = () => api.get(`/outbound/dnc-lists/${listId}`).then((r) => setRow(r.data)).catch(() => {});
  useEffect(() => { load(); }, [listId]);

  const addOne = async () => {
    if (!phone.trim()) return;
    try {
      await api.post(`/outbound/dnc-lists/${listId}/entries`, { phone: phone.trim(), reason: reason.trim() });
      setPhone(""); setReason(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeOne = async (r) => {
    if (!window.confirm(`Remove ${r.phone}?`)) return;
    try { await api.delete(`/outbound/dnc-lists/${listId}/entries/${r.dnc_id}`); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doCopy = async () => {
    if (!copySource) return;
    try {
      const { data } = await api.post(`/outbound/dnc-lists/${listId}/copy-from/${copySource}`);
      toast.success(`Copied ${data.added} entry(ies) from "${data.source_list}"${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
      setCopySource(""); load(); onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const others = (allLists || []).filter((l) => l.dnc_list_id !== listId);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ai-outbound-dnc-list-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban className="h-4 w-4 text-red-500" /> {row?.name || "DNC list"}</DialogTitle>
          <DialogDescription>{row?.entries?.length || 0} entry(ies){row?.description ? ` · ${row.description}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2" data-testid="dnc-detail-add-row">
            <Input placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addOne()} className="w-52 h-8" data-testid="dnc-detail-phone" />
            <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addOne()} className="w-52 h-8" data-testid="dnc-detail-reason" />
            <Button size="sm" onClick={addOne} disabled={!phone.trim()} className="h-8" data-testid="dnc-detail-add"><Plus className="h-3.5 w-3.5" /> Add</Button>
            {others.length > 0 && (
              <div className="ml-auto flex items-center gap-1">
                <Select value={copySource} onValueChange={setCopySource}>
                  <SelectTrigger className="h-8 w-52 text-xs" data-testid="dnc-detail-copy-select"><SelectValue placeholder="Copy from another list…" /></SelectTrigger>
                  <SelectContent>
                    {others.map((l) => <SelectItem key={l.dnc_list_id} value={l.dnc_list_id}>{l.name} ({l.entry_count || 0})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={doCopy} disabled={!copySource} className="h-8" data-testid="dnc-detail-copy-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
            )}
          </div>
          {!row ? <div className="text-slate-400">Loading…</div> : (
            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Phone</th>
                    <th className="px-2 py-1.5 text-left">Reason</th>
                    <th className="px-2 py-1.5 text-left">Added by</th>
                    <th className="px-2 py-1.5 text-left">When</th>
                    <th className="px-2 py-1.5 text-right"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(row.entries || []).map((r) => (
                    <tr key={r.dnc_id} data-testid={`dnc-detail-row-${r.dnc_id}`}>
                      <td className="px-2 py-1.5 font-medium text-slate-800">{r.phone}</td>
                      <td className="px-2 py-1.5 text-slate-600">{r.reason || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1.5 text-slate-600">{r.added_by_name || "—"}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <button onClick={() => removeOne(r)} data-testid={`dnc-detail-remove-${r.dnc_id}`}
                          className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {(row.entries || []).length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-slate-400">No entries yet — add one above or upload a CSV from the list row.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DncListsPanel({ lists, onReload }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [openId, setOpenId] = useState(null);
  const [uploading, setUploading] = useState(null);

  const createList = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/outbound/dnc-lists", { name, description: desc });
      toast.success("DNC list created");
      setName(""); setDesc(""); setCreating(false); onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delList = async (l) => {
    if (!window.confirm(`Delete DNC list "${l.name}"? Campaigns currently referencing it will be detached.`)) return;
    try { await api.delete(`/outbound/dnc-lists/${l.dnc_list_id}`); toast.success("Deleted"); onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const upload = async (l, file) => {
    if (!file) return;
    setUploading(l.dnc_list_id);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/outbound/dnc-lists/${l.dnc_list_id}/upload-csv`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added ${data.added} entry(ies)${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(null); }
  };
  const exportCsv = async (l) => {
    try {
      const { data } = await api.get(`/outbound/dnc-lists/${l.dnc_list_id}/export.csv`);
      await downloadFromUrl(`/files/${data.token}`);
      toast.success(`Exported ${data.count} row(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };

  return (
    <div className="space-y-4">
      <Card className="border border-slate-200 p-4" data-testid="ai-outbound-dnc-lists-create">
        {creating ? (
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <Input data-testid="new-dnc-list-name" placeholder="List name (e.g. Global opt-outs)" value={name}
              onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createList()} />
            <Input data-testid="new-dnc-list-desc" placeholder="Description (optional)" value={desc}
              onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createList()} />
            <Button onClick={createList} data-testid="new-dnc-list-save">Create</Button>
            <Button variant="ghost" onClick={() => { setCreating(false); setName(""); setDesc(""); }} data-testid="new-dnc-list-cancel">Cancel</Button>
          </div>
        ) : (
          <Button onClick={() => setCreating(true)} variant="outline" data-testid="new-dnc-list-btn"><Plus className="h-4 w-4" /> New DNC list</Button>
        )}
      </Card>

      {lists.length === 0 ? (
        <Card className="border border-dashed border-slate-200 p-10 text-center" data-testid="ai-outbound-dnc-lists-empty">
          <Ban className="mx-auto h-8 w-8 text-slate-300" />
          <div className="mt-2 font-heading text-sm font-semibold text-slate-700">No Do-Not-Call lists yet</div>
          <p className="mt-1 text-xs text-slate-400">Create a list, upload phone numbers, then attach it to any campaign in the builder.</p>
        </Card>
      ) : (
        <Card className="border border-slate-200 p-0 overflow-hidden" data-testid="ai-outbound-dnc-lists-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">DNC list</th>
                <th className="px-3 py-2 text-right">Entries</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lists.map((l) => (
                <tr key={l.dnc_list_id} className="hover:bg-slate-50" data-testid={`ai-outbound-dnc-list-row-${l.dnc_list_id}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => setOpenId(l.dnc_list_id)} className="text-left font-medium text-slate-800 hover:text-primary"
                      data-testid={`ai-outbound-dnc-list-open-${l.dnc_list_id}`}>{l.name}</button>
                    {l.description && <div className="text-xs text-slate-400">{l.description}</div>}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-700">{l.entry_count || 0}</td>
                  <td className="px-3 py-2 text-slate-600">{l.owner_name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <input type="file" accept=".csv" className="hidden" id={`dnc-file-${l.dnc_list_id}`}
                      onChange={(e) => upload(l, e.target.files?.[0])} data-testid={`ai-outbound-dnc-list-upload-input-${l.dnc_list_id}`} />
                    <label htmlFor={`dnc-file-${l.dnc_list_id}`}
                      className="mr-2 inline-flex cursor-pointer items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[11px] text-slate-600 hover:border-primary/40 hover:text-primary"
                      data-testid={`ai-outbound-dnc-list-upload-${l.dnc_list_id}`}>
                      {uploading === l.dnc_list_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      CSV
                    </label>
                    <button onClick={() => exportCsv(l)} data-testid={`ai-outbound-dnc-list-export-${l.dnc_list_id}`}
                      className="mr-2 text-slate-300 hover:text-primary"><Download className="h-4 w-4" /></button>
                    <button data-testid={`ai-outbound-dnc-list-delete-${l.dnc_list_id}`} onClick={() => delList(l)}
                      className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {openId && <DncListDetailDialog listId={openId} allLists={lists} onClose={() => { setOpenId(null); onReload(); }} onChanged={onReload} />}
    </div>
  );
}
